import ModalGenerateMatrix, {
  GenerateMatrixFunction,
} from '@/components/astro.ai/generate/ModalGenerateMatrix';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { useAbortController } from '@/data/hook/useAbortController';
import useSafePortal from '@/data/hook/useSafePortal';
import useToggle from '@/data/hook/useToggle';
import { lessonMatrixGenerator } from '@/data/services/astro.ai/generateServices';
import { LessonMatrixGenerate } from '@/models/astro.ai/generate/LessonMatrix';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';

type GenerateLessonDetailProps = {
  coursePathId?: number;
  lessonOrder: number;
  lessonName: string;
  disabled?: boolean;
  locked?: boolean;
  onGenerate?: (data: LessonMatrixGenerate) => void;
};

export const GenerateLessonDetail = ({
  coursePathId,
  lessonOrder,
  lessonName,
  disabled,
  locked,
  onGenerate,
}: GenerateLessonDetailProps) => {
  const createSafePortal = useSafePortal();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio.actions',
  });
  const { signal, abort } = useAbortController();
  const { isOpen, close, open } = useToggle();
  const generate: GenerateMatrixFunction = async ({
    instructions,
    materialType,
  }) => {
    if (!coursePathId) return;

    const result = await lessonMatrixGenerator(
      {
        coursePathId,
        lessonOrder,
        lessonName,
        instructions,
        materialType,
      },
      signal,
    );

    onGenerate?.(result);
  };

  const onCancel = () => {
    abort();
    close();
  };

  return (
    <Fragment>
      <ConditionalWrapper
        condition={!!locked}
        wrapper={<Tooltip text={t('generateLessonDetail.locked')} />}
      >
        <MainButton
          onClick={open}
          icon={<SparklesIcon className="w-4 h-4" />}
          text={t('generateLessonDetail.name')}
          disabled={disabled || locked || !coursePathId}
        />
      </ConditionalWrapper>
      {createSafePortal(
        <ModalGenerateMatrix
          visible={isOpen}
          onClose={onCancel}
          onGenerate={generate}
          hasValue={false}
          showMaterialType
        />,
      )}
    </Fragment>
  );
};
